import React, { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical, Eye, EyeOff, Save, Sparkles } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminValues() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = () =>
    api.get("/admin/brand-values")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const setLocal = (id, patch) =>
    setItems((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));

  const saveOne = async (v) => {
    setSaving((s) => ({ ...s, [v.id]: true }));
    try {
      await api.put(`/admin/brand-values/${v.id}`, {
        text_it: v.text_it,
        text_en: v.text_en || "",
        active: v.active,
        order: v.order,
      });
      toast.success(`"${v.text_it}" salvato`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving((s) => ({ ...s, [v.id]: false })); }
  };

  const add = async () => {
    try {
      const r = await api.post("/admin/brand-values", { text_it: "Nuovo valore", text_en: "", active: true, order: items.length });
      setItems((prev) => [...prev, r.data]);
      toast.success("Valore aggiunto");
    } catch { toast.error("Errore aggiunta"); }
  };

  const remove = async (id) => {
    const v = items.find((x) => x.id === id);
    if (!window.confirm(`Eliminare il valore "${v?.text_it}"?`)) return;
    await api.delete(`/admin/brand-values/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
    toast.success("Rimosso");
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    // Reindex order
    const withOrder = reordered.map((v, i) => ({ ...v, order: i }));
    setItems(withOrder);
    try {
      await api.post("/admin/brand-values/reorder", { ids: withOrder.map((v) => v.id) });
      toast.success("Ordine aggiornato");
    } catch { toast.error("Errore riordinamento"); load(); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="admin-values-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Contenuti home</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2 flex items-center gap-3">
            <Sparkles size={38} className="text-[#FF5A1F]" strokeWidth={1.5} /> Valori Perla
          </h1>
          <p className="text-[#0A0A0A]/60 mt-2 max-w-2xl leading-relaxed">
            Le parole chiave che compaiono nella sezione <strong>"Perla Music in poche parole"</strong> in home. Trascinale per riordinarle, spuntale per nasconderle temporaneamente. Ogni valore ha una versione IT e una EN.
          </p>
        </div>
        <button onClick={add} className="btn-cta" data-testid="values-add-btn">
          <Plus size={16} /> Aggiungi valore
        </button>
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/50">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/20 p-12 text-center text-[#0A0A0A]/50">
          Nessun valore configurato. Aggiungi il primo.
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="values">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                {items.map((v, idx) => (
                  <Draggable key={v.id} draggableId={v.id} index={idx}>
                    {(dp, snap) => (
                      <div
                        ref={dp.innerRef}
                        {...dp.draggableProps}
                        className={`border-2 bg-[#FAFAF7] p-4 flex flex-wrap items-center gap-3 transition-shadow ${snap.isDragging ? "border-[#FF5A1F] shadow-2xl" : "border-[#0A0A0A]"}`}
                        data-testid={`value-row-${v.id}`}
                      >
                        <div {...dp.dragHandleProps} className="text-[#0A0A0A]/40 cursor-grab active:cursor-grabbing shrink-0" title="Trascina per riordinare">
                          <GripVertical size={18} />
                        </div>
                        <div className="w-8 h-8 border-2 border-[#0A0A0A] bg-[#FF5A1F] text-[#FAFAF7] font-bold text-sm flex items-center justify-center shrink-0">{idx + 1}</div>

                        <div className="flex-1 min-w-[220px] grid grid-cols-2 gap-2">
                          <input
                            value={v.text_it}
                            onChange={(e) => setLocal(v.id, { text_it: e.target.value })}
                            placeholder="Italiano"
                            className="border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm font-bold"
                            data-testid={`value-it-${v.id}`}
                          />
                          <input
                            value={v.text_en || ""}
                            onChange={(e) => setLocal(v.id, { text_en: e.target.value })}
                            placeholder="English"
                            className="border-2 border-[#0A0A0A]/40 py-2 px-3 bg-white text-sm"
                            data-testid={`value-en-${v.id}`}
                          />
                        </div>

                        <button
                          onClick={() => setLocal(v.id, { active: !v.active })}
                          className={`p-2 border-2 transition-colors ${v.active ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-[#0A0A0A]/30 text-[#0A0A0A]/40"}`}
                          title={v.active ? "Visibile in home" : "Nascosto"}
                          data-testid={`value-toggle-${v.id}`}
                        >
                          {v.active ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>

                        <button
                          onClick={() => saveOne(v)}
                          disabled={saving[v.id]}
                          className="btn-cta py-2 px-3 text-xs disabled:opacity-60"
                          data-testid={`value-save-${v.id}`}
                        >
                          <Save size={12} /> {saving[v.id] ? "…" : "Salva"}
                        </button>

                        <button
                          onClick={() => remove(v.id)}
                          className="p-2 border border-[#0A0A0A]/20 text-[#0A0A0A]/50 hover:border-red-600 hover:text-red-600"
                          data-testid={`value-delete-${v.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <div className="mt-8 border-2 border-[#FF5A1F]/40 bg-[#FF5A1F]/5 p-4 text-sm text-[#0A0A0A]/80">
        <strong className="text-[#FF5A1F]">Suggerimento:</strong> tieni tra 6 e 12 valori — meno di 6 la sezione appare vuota, oltre 12 diventa affollata. Riordinali per mettere i più importanti in alto (verranno mostrati con font più grande).
      </div>
    </div>
  );
}
